import { AccountingProvider, UserProvider } from "../client";
import type { AppHost } from "../components/appHost";
import { Router } from "./router";
import { GlobalOptions, Meta } from "../types";
import { Application, ApplicationCollection } from "./application";
import { Auth } from "./auth";
import { Theme } from "./theme";
import { ServiceCollection } from "./services";
import { Telemetry } from "./telemetry";
import { Localization } from "./localization";
import { Logging } from "./logging";
import { Commands } from "./commands";
import { Settings } from "./settings";
import { Broadcast } from "./broadcast";

export interface ContainerItem {
	container: Container;
}

export class ContainerItemCollection<TItem> implements ContainerItem {

	container: Container;

	#items: Map<string, TItem> = new Map();

	register(name: string, service: TItem) {
		(service as ContainerItem).container = this.container;
		this.#items.set(name, service);
	}

	deregister(name: string) {
		this.#items.delete(name);
	}

	get(name: string) {
		return this.#items.get(name);
	}

	forEach(callback: (service: TItem) => void) {
		this.#items.forEach(callback);
	}

}

export class Container {

	auth: Auth;
	host: AppHost;
	#router: Router;
	get router() { return this.#router }
	set router(value: Router) {
		this.#router = value;
		value.addEventListener('route', () => this.#loadDB());
		this.#loadDB();
	}
	services = new ServiceCollection(this);
	applications = new ApplicationCollection(this);
	theme = new Theme(this);
	telemetry = new Telemetry(this);
	localization = new Localization(this);
	log = new Logging(this);
	commands = new Commands(this);
	settings = new Settings(this);
	broadcast = new Broadcast(this);
	options: GlobalOptions;
	meta: Meta;

	create: () => Promise<void>;

	#db = 1;
	get db() { return this.#db }

	#accountingProvider: AccountingProvider;
	get accountingProvider() { return this.#accountingProvider }
	set accountingProvider(value: AccountingProvider) {
		this.#accountingProvider = value;
		this.services.forEach(s => s.accountingProvider = value);
	}

	#userProvider: UserProvider;
	get userProvider() { return this.#userProvider }
	set userProvider(value: UserProvider) {
		this.#userProvider = value;
		this.services.forEach(s => s.userProvider = value);
	}

	_onLanguageChange = () => {
		if (this.host) this.create();
	}

	async init() {
		this.localization.removeEventListener('change', this._onLanguageChange);
		this.localization.addEventListener('change', this._onLanguageChange);
	}

	ms(name: string) {
		return this.services.get(name);
	}

	#lsDbKey() {
		return `${this.router?.options?.settingsPrefix ?? 'rp'}-db-${this.#accountingProvider?.a ?? 0}`;
	}

	#loadDB() {
		const stored = localStorage.getItem(this.#lsDbKey());
		this.#db = stored ? parseInt(stored) : 1;
	}

	async setDB(db: number, refreshPage = false) {
		this.#db = db;
		if (db === 1) localStorage.removeItem(this.#lsDbKey());
		else localStorage.setItem(this.#lsDbKey(), db.toString());
		if (refreshPage && this.host) await this.create();
	}

	async app<TApp extends Application = Application>(name: string): Promise<TApp> {
		const app = this.applications.get<TApp>(name);
		if (app && !app.initialized) await app.init(this);
		return app;
	}

}
